import { useWhiteLabel } from '@allesauto/core/whiteLabel';
import {
  FieldTypeAsset,
  FieldTypeHTML,
  FieldTypeLink,
  TextComponentBackground,
  TextComponentPostion,
  TextComponentTypesLeft,
} from '@allesauto/utils/consts';
import {
  createMarkup,
  getUrlFromStoryblokLink,
  isWhiteLabel,
  parseSlashToURL,
} from '@allesauto/utils/funcs';
import { getPcpUrlForContentPages } from '@allesauto/utils/routeUtils';
import {
  ITypeListMarker,
  ITypeListMarkerColor,
} from '@allesauto/utils/storyblok';
import { ArrowRight16 } from '@carbon/icons-react';
import { renderRichText } from '@storyblok/react';
import { sbEditable } from '@storyblok/storyblok-editable';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import React, { FC, ReactNode, useEffect, useState } from 'react';

import { TextComponentAdapter } from './textComponentAdapter';
import Button from '../button';
import NextImage from '../nextImage';
import Testimonial, { TestimonialProps } from '../testimonial';
import TextLink from '../textLink';

export { TextComponentAdapter };

const MIN_VALUE_LEASING_FILTER = 124;
const MAX_VALUE_LEASING_FILTER = 1113;

export interface SectionAreaBase {
  linkLabel?: string;
  link?: FieldTypeLink;
  linkIsOverlay?: boolean;
  linkAsButton?: boolean;
  filterLeasing?: boolean;
  component?: string;
  listItemMarker?: ITypeListMarker;
  listItemMarkerColor?: ITypeListMarkerColor;
}

export interface SectionAreaWithBody extends SectionAreaBase {
  body: string;
  bodyHtml?: never;
  bodyHtml_wl?: never;
}

export interface SectionAreaWithHtml extends SectionAreaBase {
  body?: never;
  bodyHtml: string;
  bodyHtml_wl?: string;
}

export interface TestimonialComponent {
  testimonial: TestimonialProps;
  position: TextComponentPostion | string;
}

export interface TextComponentLeftProps {
  isHeaderH1?: boolean;
  type: TextComponentTypesLeft | string;
  body: string | undefined;
  image: FieldTypeAsset;
}

export interface TextComponentInterface {
  leftArea?: TextComponentLeftProps;
  sectionArea?: (SectionAreaWithBody | SectionAreaWithHtml)[];
  testimonialComponent?: TestimonialComponent;
}

export interface ITextComponents {
  textComponents: TextComponentInterface[];
  background?: TextComponentBackground;
  handleAction?: () => void;
  component: string;
  _uid: string;
  _editable?: string;
}

const TextComponent: FC<ITextComponents> = (props: ITextComponents) => {
  const {
    background = TextComponentBackground.DEFAULT,
    textComponents,
    handleAction,
  } = props;

  const hasGreyBackground =
    background && background === TextComponentBackground.GREY
      ? 'bg-grey10'
      : '';

  const [currentHref, setCurrentHref] = useState<string | URL>('');
  const router = useRouter();
  const whiteLabel = useWhiteLabel();

  const gotoURL = (link?: FieldTypeLink) => {
    const url = link ? getUrlFromStoryblokLink(link) : '#';
    if (link?.target === '_blank') {
      window.open(url, '_blank');
    } else {
      router.push(parseSlashToURL(url));
    }
  };

  const getLeftSideComponent = (objectComponent: TextComponentInterface) => {
    const className = {
      headerTag: clsx(
        'break-words col-start-2 pb-40px sm:col-end-24 md:col-end-12',
        objectComponent.leftArea?.isHeaderH1 && 'text-headline2'
      ),
    };

    switch (objectComponent.leftArea && objectComponent.leftArea.type) {
      case TextComponentTypesLeft.TITLE: {
        if (objectComponent.leftArea?.isHeaderH1) {
          return (
            <h1 className={className.headerTag}>
              {objectComponent.leftArea &&
                (objectComponent.leftArea.body as ReactNode)}
            </h1>
          );
        } else {
          return (
            <h2 className={className.headerTag}>
              {objectComponent.leftArea &&
                (objectComponent.leftArea.body as ReactNode)}
            </h2>
          );
        }
      }
      case TextComponentTypesLeft.NONE: {
        return <div className="col-start-2 sm:col-end-24 md:col-end-12"></div>;
      }
      case TextComponentTypesLeft.TESTIMONIAL: {
        const className =
          objectComponent?.testimonialComponent?.position ===
          TextComponentPostion.LEFT
            ? 'col-start-2 sm:col-end-24 md:col-end-12 sm:pb-40px'
            : 'sm:col-start-2 md:col-start-14 col-end-24';
        return (
          <div className={className}>
            {objectComponent?.testimonialComponent?.testimonial && (
              <Testimonial
                {...objectComponent?.testimonialComponent?.testimonial}
              />
            )}
          </div>
        );
      }
      case TextComponentTypesLeft.PICTURE: {
        return (
          <div className="aspect-[16/10] col-start-2 relative sm:col-end-24 md:col-end-12">
            {objectComponent.leftArea?.image?.filename && (
              <NextImage
                useCustomLoader={false}
                alt={objectComponent.leftArea?.image?.alt}
                layout="fill"
                src={objectComponent.leftArea?.image?.filename}
                objectFit="cover"
              />
            )}
          </div>
        );
      }
    }
  };

  useEffect(() => {
    setCurrentHref(window.location.href);
  }, []);

  const constructHref = (
    link: FieldTypeLink,
    filterLeasing: boolean | undefined
  ): string => {
    const linkUrl = getUrlFromStoryblokLink(link);
    if (currentHref && filterLeasing) {
      const url = getPcpUrlForContentPages(
        {
          key: 'LEASING_RATE_RANGE',
          min: MIN_VALUE_LEASING_FILTER,
          max: MAX_VALUE_LEASING_FILTER,
        },
        currentHref as string,
        linkUrl
      );
      return url;
    }
    return parseSlashToURL(linkUrl);
  };

  return (
    <div
      {...sbEditable(props)}
      className={`sm:pt-40px md:pt-80px ${hasGreyBackground}`}
    >
      {textComponents &&
        textComponents.map((tComponent, index) => {
          return (
            <div
              key={index}
              className="grid grid-cols-24 sm:last:pb-0 sm:pb-40px md:pb-80px"
            >
              {getLeftSideComponent(tComponent)}

              <div
                className={clsx(
                  'relative sm:col-end-24 sm:col-start-2 md:col-end-24 md:col-start-14'
                )}
              >
                {tComponent.sectionArea &&
                  tComponent.sectionArea.map((sArea, i) => {
                    return (
                      <div key={i} className="sm:pb-40px md:pb-80px last:pb-0">
                        {'bodyHtml' in sArea && sArea.bodyHtml ? (
                          <div
                            className={clsx(
                              `rich-text rich-text-list-marker-${
                                sArea.listItemMarker
                                  ? sArea.listItemMarker
                                  : 'bullet'
                              }-${
                                sArea.listItemMarkerColor
                                  ? sArea.listItemMarkerColor
                                  : 'purple'
                              }`
                            )}
                            dangerouslySetInnerHTML={createMarkup(
                              (sArea.bodyHtml as unknown as FieldTypeHTML).type
                                ? renderRichText(
                                    isWhiteLabel(whiteLabel)
                                      ? (sArea.bodyHtml_wl as unknown as FieldTypeHTML)
                                      : (sArea.bodyHtml as unknown as FieldTypeHTML)
                                  )
                                : isWhiteLabel(whiteLabel)
                                ? String(sArea.bodyHtml_wl)
                                : sArea.bodyHtml
                            )}
                          ></div>
                        ) : (
                          <p className={clsx(sArea.linkLabel && 'pb-20px')}>
                            {sArea.body}
                          </p>
                        )}
                        {!sArea.linkAsButton && sArea.linkLabel ? (
                          sArea.linkIsOverlay ? (
                            <TextLink
                              text={sArea.linkLabel}
                              icon={<ArrowRight16 />}
                              onClick={handleAction}
                            />
                          ) : (
                            <TextLink
                              text={sArea.linkLabel}
                              icon={<ArrowRight16 />}
                              href={
                                sArea.link?.url || sArea.link?.cached_url
                                  ? constructHref(
                                      sArea.link,
                                      sArea.filterLeasing
                                    )
                                  : '#'
                              }
                              target={sArea.link?.target ?? '_self'}
                            />
                          )
                        ) : (
                          ''
                        )}
                        {sArea.linkAsButton && sArea.linkLabel && (
                          <Button
                            customClassName="mt-20px"
                            text={sArea.linkLabel}
                            onClick={() => gotoURL(sArea.link)}
                          />
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          );
        })}
    </div>
  );
};

export default TextComponent;
